import { create } from "zustand";
import {
  startRecording as recorderStart,
  stopRecording as recorderStop,
  isRecording as recorderIsRecording,
  getStartTime as recorderGetStartTime,
  resumeIfActive,
} from "../utils/issueRecorder";

// Hydrate recording state from localStorage (survives page reloads)
const initialRecording = recorderIsRecording();
const initialStartTime = initialRecording ? recorderGetStartTime() : null;

// Re-install hooks if recording was active before page reload
if (initialRecording) {
  resumeIfActive();
}

const useSupportStore = create((set, get) => ({
  drawerOpen: false,
  activeTab: "create",
  tickets: [],
  selectedTicketId: null,
  selectedTicketDetail: null,
  unreadCount: 0,
  isSubmitting: false,
  isLoading: false,
  annotating: false,
  annotatedScreenshot: null, // base64 JPEG (no prefix)

  // Recording state — hydrated from localStorage on init
  isRecording: initialRecording,
  recordingStartTime: initialStartTime,
  recordingData: null, // populated after stopRecording()

  startRecording: () => {
    recorderStart();
    set({ isRecording: true, recordingStartTime: Date.now(), recordingData: null });
  },

  stopRecording: () => {
    const data = recorderStop();
    set({ isRecording: false, recordingStartTime: null, recordingData: data });
    return data;
  },

  clearRecordingData: () => set({ recordingData: null }),

  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false, selectedTicketId: null, selectedTicketDetail: null }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  capturedBase64: null, // raw screenshot captured before overlay shows
  startAnnotation: async () => {
    // Close drawer first, then capture screenshot BEFORE showing overlay
    set({ drawerOpen: false });
    await new Promise((r) => setTimeout(r, 400)); // wait for drawer close animation
    try {
      const base64 = await window.electronAPI.support.captureScreenshot();
      set({ annotating: true, capturedBase64: base64 });
    } catch {
      set({ drawerOpen: true }); // reopen on failure
    }
  },
  finishAnnotation: (base64) => set({ annotating: false, capturedBase64: null, annotatedScreenshot: base64, drawerOpen: true }),
  cancelAnnotation: () => set({ annotating: false, capturedBase64: null, drawerOpen: true }),
  clearAnnotation: () => set({ annotatedScreenshot: null }),

  loadTickets: async () => {
    if (!window.electronAPI?.support) return;
    set({ isLoading: true });
    try {
      const tickets = await window.electronAPI.support.getTickets();
      set({ tickets, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  submitTicket: async (data) => {
    if (!window.electronAPI?.support) return { success: false, error: "Not in Electron" };
    set({ isSubmitting: true });
    try {
      const result = await window.electronAPI.support.createTicket(data);
      if (result.success) {
        // Refresh tickets list
        const tickets = await window.electronAPI.support.getTickets();
        set({ isSubmitting: false, tickets, activeTab: "list" });
      } else {
        set({ isSubmitting: false });
      }
      return result;
    } catch (err) {
      set({ isSubmitting: false });
      return { success: false, error: err.message };
    }
  },

  selectTicket: async (localId) => {
    if (!window.electronAPI?.support) return;
    set({ selectedTicketId: localId });
    try {
      const detail = await window.electronAPI.support.getTicketDetail(localId);
      set({ selectedTicketDetail: detail });
    } catch {}
  },

  addComment: async (localId, content) => {
    if (!window.electronAPI?.support) return;
    try {
      await window.electronAPI.support.addComment(localId, content);
      // Refresh detail
      const detail = await window.electronAPI.support.getTicketDetail(localId);
      set({ selectedTicketDetail: detail });
    } catch {}
  },

  updateFromServer: () => {
    // Called when we receive updates via IPC
    get().loadTickets();
    if (get().selectedTicketId) {
      get().selectTicket(get().selectedTicketId);
    }
  },
}));

export default useSupportStore;
